import queryString from 'querystring';
import FetchStatus from '../../common/types/FetchStatus';
import { PageResponse, Paginated } from '../../common/types/Paginated';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../configureStore';
import apiService from '../../common/services/api/apiService';
import { IntermedaryStatuses } from '../../domain/types/IntermediaryStatuses';
import ChsIntermediary from '../../domain/types/ChsIntermediary';
import RejectionReason from 'domain/types/RejectionReason';

export type RegisteredApp = {
  appId: number;
  registrationNumber: string;
  groupRefKey: string;
  expirationDate: string;
  address: string;
  statusCd: string;
  statusDescr: string;
  rejectionReasons: RejectionReason[];
  externalRefKey: string;
  renewalPrice: string;
  intermediaries: ChsIntermediary[];
  intermediaryStatuses: IntermedaryStatuses;
  canCheckout: boolean;
};

type RegisteredAppState = {
  entities: {
    [key: string]: RegisteredApp;
  };
  meta: {
    currentPageIds: number[];
    fetchStatus: FetchStatus;
    pagination: Paginated;
    sortBy: string;
    sortDir: string;
  };
};

export const initialState: RegisteredAppState = {
  entities: {},
  meta: {
    currentPageIds: [],
    fetchStatus: FetchStatus.none,
    pagination: {
      totalPages: 0,
      totalCount: 0,
      currentPage: 0,
      itemsPerPage: 10,
    },
    sortBy: 'REGISTRATION_NUMBER',
    sortDir: 'ASC',
  },
};

const RegisteredAppSlice = createSlice({
  name: 'RegisteredApps',
  initialState,
  reducers: {
    fetchRegisteredAppsStart(state) {
      state.meta.fetchStatus = FetchStatus.loading;
      return state;
    },
    fetchRegisteredAppsUpdateSorting(
      state,
      action: PayloadAction<{ sortBy: string; sortDir: string }>
    ) {
      state.meta.sortBy = action.payload.sortBy;
      state.meta.sortDir = action.payload.sortDir;
      return state;
    },
    fetchRegisteredAppsSuccess(
      state,
      action: PayloadAction<PageResponse<RegisteredApp>>
    ) {
      state.meta.fetchStatus = FetchStatus.loaded;
      state.meta.currentPageIds = [];
      for (const app of action.payload.items) {
        state.entities[app.appId] = app;
        state.meta.currentPageIds.push(app.appId);
      }
      state.meta.pagination = {
        currentPage: action.payload.currentPage,
        itemsPerPage: action.payload.itemsPerPage,
        totalCount: action.payload.totalCount,
        totalPages: action.payload.totalPages,
      };
      return state;
    },
    getRegisteredAppsSuccess(
      state,
      action: PayloadAction<{ registeredApp: RegisteredApp }>
    ) {
      const { registeredApp } = action.payload;
      state.entities[registeredApp.appId] = registeredApp;
      return state;
    },
  },
});

export type RegisteredAppReq = {
  pageNum: number;
  pageSize: number;
  sortBy: string;
  sortDir: string;
};

export function fetchRegisteredApps(
  req: RegisteredAppReq
): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    dispatch(RegisteredAppSlice.actions.fetchRegisteredAppsStart());

    const {
      auth: { access_token },
    } = getState();

    let error;

    try {
      const query = queryString.stringify(req);
      const res = await apiService.get(
        `/applications/registered?${query}`,
        access_token
      );
      if (res.ok) {
        const pagedApplications = await res.json();

        dispatch(
          RegisteredAppSlice.actions.fetchRegisteredAppsUpdateSorting({
            sortDir: req.sortDir,
            sortBy: req.sortBy,
          })
        );
        dispatch(
          RegisteredAppSlice.actions.fetchRegisteredAppsSuccess(
            pagedApplications
          )
        );
        return Promise.resolve();
      }
      error = await res.json();
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
      console.log(e);
    }

    return Promise.reject(error);
  };
}

export default RegisteredAppSlice.reducer;
